import { useState, useEffect, useRef } from 'react';

import TopNav from './TopNav';
import HeroSection from './HeroSection';
import DesktopSidePanel from './DesktopSidePanel';
import StickyCompactHeader from './StickyCompactHeader';
import AnchorTabs from './AnchorTabs';
import BrandStory from './BrandStory';
import SummarySection from './SummarySection';
import ReviewPhotos from './ReviewPhotos';
import ReviewSummary from './ReviewSummary';
import LookbookSection from './LookbookSection';
import ServiceCards from './ServiceCards';
import FaqSection from './FaqSection';
import InfoSection from './InfoSection';
import BottomCTA from './BottomCTA';
import PackageBenefitsSection from './PackageBenefitsSection';

const Detail = (props) => {
  const {
    shop,
    heroImages,
    brandStory,
    verifiedReasonsJson,
    promotionBenefits,
    reviewPhotos,
    reviewSummary,
    brandLookbook,
    products,
    faqs,
    information,
  } = props;

  const [activeTab, setActiveTab] = useState('summary');
  const summaryRef = useRef<HTMLDivElement>(null);
  const photosRef = useRef<HTMLDivElement>(null);
  const reviewsRef = useRef<HTMLDivElement>(null);
  const infoRef = useRef<HTMLDivElement>(null);
  const [showCompactHeader, setShowCompactHeader] = useState(false);

  const sectionMap: Record<string, React.RefObject<HTMLDivElement | null>> = {
    summary: summaryRef,
    photos: photosRef,
    reviews: reviewsRef,
    info: infoRef,
  };

  const handleTabClick = (id: string) => {
    setActiveTab(id);

    const ref = sectionMap[id];

    if (ref?.current) {
      const top = ref.current.offsetTop - 100;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowCompactHeader(window.scrollY > 200);

      const scrollY = window.scrollY + 140;
      const ids = ['summary', 'photos', 'reviews', 'info'];

      for (let i = ids.length - 1; i >= 0; i -= 1) {
        const id = ids[i];
        const el = sectionMap[id]?.current;

        if (el && el.offsetTop <= scrollY) {
          setActiveTab(id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-[100dvh] bg-[#F7F2EA] pb-24 lg:pb-0">
      <div className="lg:hidden bg-white">
        <TopNav title={shop.name} />
        <HeroSection shop={shop} images={heroImages} />
        <AnchorTabs activeTab={activeTab} onTabClick={handleTabClick} />
      </div>

      <div className="hidden lg:block bg-white">
        <div className="lg:max-w-[1200px] lg:mx-auto lg:px-6 lg:pt-6">
          <TopNav title={shop.name} />
          <HeroSection shop={shop} images={heroImages} />

          <div className="py-6">
            <h1 className="text-2xl md:text-[28px] font-bold text-[#121B35] leading-tight">
              {shop.name}
            </h1>

            <div className="flex gap-2 mt-2.5">
              {shop.category?.map((categoryName) => (
                <span
                  key={categoryName}
                  className="bg-[#F7F2EA] text-[#121B35] text-xs md:text-sm px-2.5 py-1 rounded-full font-medium"
                >
                  {categoryName}
                </span>
              ))}
            </div>

            <p className="text-sm md:text-base text-[#536079] mt-3 leading-relaxed">
              {shop.description}
            </p>

            <div className="flex items-center gap-2 mt-3 text-xs md:text-sm text-[#536079]">
              <span>{shop.location}</span>
              <span className="text-[#E8E2DA]">·</span>
              <span>실제 후기 {shop.reviewCount}개</span>
              <span className="text-[#E8E2DA]">·</span>
              <span>후기 사진 {shop.photoCount}장</span>
            </div>

            {shop.verified && (
              <div className="mt-3 inline-flex items-center gap-1.5 bg-[#121B35] text-white text-xs md:text-sm px-3 py-1.5 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-[#B9965B]" />
                다이렉트웨딩 후기 기반 검증 업체
              </div>
            )}
          </div>

          <AnchorTabs activeTab={activeTab} onTabClick={handleTabClick} />
        </div>
      </div>

      <StickyCompactHeader
        activeTab={activeTab}
        onTabClick={handleTabClick}
        show={showCompactHeader}
        shop={shop}
      />

      <div className="lg:max-w-[1200px] lg:mx-auto lg:px-6 lg:pt-6">
        <div className="lg:flex lg:gap-8 lg:items-start">
          <div className="lg:flex-1 lg:min-w-0 lg:max-w-[800px]">
            <div className="px-4 lg:px-0">
              <div ref={summaryRef} className="pt-4 lg:pt-0">
                
                <SummarySection introText={verifiedReasonsJson.introText} items={verifiedReasonsJson.items} />
                <PackageBenefitsSection data={promotionBenefits} />
              </div>

              <div ref={photosRef} className="pt-4 lg:pt-0">
                <ReviewPhotos photos={reviewPhotos} totalCount={shop.photoCount} />
              </div>

              <div ref={reviewsRef} className="pt-4 lg:pt-0">
                <ReviewSummary data={reviewSummary} hideDescription />
              </div>

              <div className="pt-4 lg:pt-0">
                <LookbookSection images={brandLookbook} />
              </div>

              <div className="pt-4 lg:pt-0">
                <ServiceCards products={products} />
              </div>

              <div className="pt-4 lg:pt-0">
                <FaqSection faqs={faqs} />
              </div>

              <div ref={infoRef} className="pt-4 lg:pt-0">
                <BrandStory data={brandStory} />
                <InfoSection information={information} shop={shop} />
              </div>
            </div>
          </div>

          <div className="hidden lg:block lg:w-[340px] lg:shrink-0">
            <div className="sticky top-[80px]">
              <DesktopSidePanel shop={shop} />
            </div>
          </div>
        </div>
      </div>

      <BottomCTA />

    </div>
  );
};

export default Detail;